import { forwardRef, useId, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes, type CSSProperties, type KeyboardEvent } from "react";
import { Icon } from "./icons.js";
import { Tooltip } from "./floating.js";

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/* ------------------------------ Button ------------------------------ */

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "primary" | "ghost" | "danger";
  size?: "sm" | "md";
  icon?: string;
  active?: boolean;
  tooltip?: ReactNode;
  shortcut?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ variant = "default", size = "md", icon, active, tooltip, shortcut, className, children, type = "button", ...rest }, ref) {
  // An icon-only button has no text for a screen reader to read, and a tooltip
  // is not a name — it only exists while a pointer hovers it. Where the caller
  // gave a plain-text tooltip and no explicit label, that text is the name.
  const iconOnly = !children && !!icon;
  const label = rest["aria-label"] ?? (iconOnly && typeof tooltip === "string" ? tooltip : undefined);
  const btn = (
    <button ref={ref} type={type} className={cx("bpe-btn", variant !== "default" && `bpe-btn--${variant}`, size === "sm" && "bpe-btn--sm", iconOnly && "bpe-btn--icon", active && "bpe-btn--active", className)} {...rest} aria-label={label}>
      {icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
  if (tooltip) return <Tooltip content={tooltip} shortcut={shortcut}>{btn}</Tooltip>;
  return btn;
});

/* ------------------------------- Inputs ------------------------------ */

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  value: string;
  onChange: (value: string) => void;
  /** Commit on blur/enter instead of every keystroke. */
  lazy?: boolean;
  invalid?: boolean;
  suffix?: ReactNode;
  mono?: boolean;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput({ value, onChange, lazy, invalid, suffix, mono, className, onBlur, onKeyDown, ...rest }, ref) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = lazy && draft !== null ? draft : value;
  const input = (
    <input
      ref={ref}
      className={cx("bpe-input", invalid && "bpe-input--invalid", mono && "bpe-expr", className)}
      value={shown}
      onChange={(e) => (lazy ? setDraft(e.target.value) : onChange(e.target.value))}
      onBlur={(e) => {
        if (lazy && draft !== null && draft !== value) onChange(draft);
        setDraft(null);
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (lazy && e.key === "Enter") {
          if (draft !== null && draft !== value) onChange(draft);
          setDraft(null);
          (e.target as HTMLInputElement).blur();
        }
        if (e.key === "Escape") {
          setDraft(null);
          (e.target as HTMLInputElement).blur();
        }
        onKeyDown?.(e);
      }}
      {...rest}
    />
  );
  if (!suffix) return input;
  return (
    <div className="bpe-input-group">
      {input}
      <span className="bpe-input-group__suffix">{suffix}</span>
    </div>
  );
});

export interface NumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  allowEmpty?: boolean;
}

export function NumberInput({ value, onChange, min, max, step = 1, unit, allowEmpty = true, className, ...rest }: NumberInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft !== null ? draft : value === null || value === undefined ? "" : String(value);
  const commit = (raw: string) => {
    setDraft(null);
    if (raw.trim() === "") {
      if (allowEmpty) onChange(null);
      return;
    }
    let n = Number(raw);
    if (!Number.isFinite(n)) return;
    if (min !== undefined) n = Math.max(min, n);
    if (max !== undefined) n = Math.min(max, n);
    if (n !== value) onChange(n);
  };
  return (
    <div className="bpe-input-group bpe-number">
      <input
        type="text"
        inputMode="decimal"
        className={cx("bpe-input", className)}
        value={shown}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit((e.target as HTMLInputElement).value);
          if (e.key === "Escape") setDraft(null);
          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault();
            const base = draft !== null ? Number(draft) : value ?? 0;
            const delta = (e.key === "ArrowUp" ? 1 : -1) * (e.shiftKey ? step * 10 : step);
            commit(String(Math.round((base + delta) * 1000) / 1000));
          }
        }}
        {...rest}
      />
      {unit ? <span className="bpe-input-group__suffix">{unit}</span> : null}
    </div>
  );
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "value"> {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  placeholder?: string;
}

export function Select({ value, onChange, options, placeholder, className, ...rest }: SelectProps) {
  return (
    <select className={cx("bpe-select", className)} value={value} onChange={(e) => onChange(e.target.value)} {...rest}>
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((o) => (
        <option key={String(o.value)} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function TextArea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx("bpe-textarea", className)} {...rest} />;
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: string; disabled?: boolean }) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} className="bpe-switch" disabled={disabled} onClick={() => onChange(!checked)} />;
}

export interface SegmentedOption {
  value: string;
  label: string;
  icon?: string;
  title?: string;
}

/**
 * A row of mutually exclusive choices.
 *
 * `exclusive` switches it from a group of independent toggles to a radio group,
 * which is what Design/Preview actually is: a screen reader then says "Design,
 * radio button, 1 of 2, selected" instead of announcing two separate buttons
 * that each happen to be pressed or not. Radio semantics also come with the
 * expectation of arrow-key navigation, so that is implemented rather than left
 * as an unmet promise, and only the selected option stays in the tab order.
 */
export function Segmented({ value, onChange, options, className, iconOnly, ariaLabel, exclusive }: { value: string; onChange: (v: string) => void; options: SegmentedOption[]; className?: string; iconOnly?: boolean; ariaLabel?: string; exclusive?: boolean }) {
  const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value));
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!exclusive) return;
    const delta = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!delta) return;
    e.preventDefault();
    const next = options[(index + delta + options.length) % options.length]!;
    onChange(next.value);
    const buttons = e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(".bpe-segmented__item");
    buttons?.[(index + delta + options.length) % options.length]?.focus();
  };
  return (
    <div className={cx("bpe-segmented", className)} role={exclusive ? "radiogroup" : "group"} aria-label={ariaLabel}>
      {options.map((o, i) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            className="bpe-segmented__item"
            role={exclusive ? "radio" : undefined}
            aria-checked={exclusive ? selected : undefined}
            aria-pressed={exclusive ? undefined : selected}
            tabIndex={exclusive ? (i === selectedIndex ? 0 : -1) : undefined}
            title={o.title ?? (iconOnly ? o.label : undefined)}
            aria-label={iconOnly ? o.label : undefined}
            onKeyDown={(e) => onKeyDown(e, i)}
            onClick={() => onChange(o.value)}
          >
            {o.icon ? <Icon name={o.icon} size={13} /> : null}
            {iconOnly && o.icon ? null : o.label}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------- Field ------------------------------ */

/**
 * A labelled control.
 *
 * The label column is 96px, which is enough for "Width" and not for "Message
 * when empty" — that one was reaching the user as "Message when em…". Rather
 * than widen every row for the sake of the longest label, a label that will not
 * fit moves above its control, which is where a long label belongs anyway.
 */
const LABEL_FITS = 17;

export function Field({ label, children, hint, error, stacked, htmlFor }: { label: ReactNode; children: ReactNode; hint?: ReactNode; error?: ReactNode; stacked?: boolean; htmlFor?: string }) {
  const id = useId();
  const tooLong = typeof label === "string" && label.length > LABEL_FITS;
  return (
    <div className={cx("bpe-field", (stacked || tooLong) && "bpe-field--stacked")}>
      <label className="bpe-field__label" htmlFor={htmlFor ?? id} title={typeof label === "string" ? label : undefined}>
        {label}
      </label>
      <div className="bpe-field__control">{children}</div>
      {hint ? <div className="bpe-field__hint">{hint}</div> : null}
      {error ? <div className="bpe-field__error">{error}</div> : null}
    </div>
  );
}

/* ------------------------------ Section ----------------------------- */

export function Section({ title, children, defaultOpen = true, actions, icon }: { title: ReactNode; children: ReactNode; defaultOpen?: boolean; actions?: ReactNode; icon?: string }) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <div className="bpe-section">
      <div className="bpe-section__head">
        <button type="button" className="bpe-section__toggle" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
          {icon ? <Icon name={icon} size={13} /> : null}
          <span>{title}</span>
          <Icon name="chevron-down" className="bpe-section__chev" />
        </button>
        {actions ? <span className="bpe-section__actions">{actions}</span> : null}
      </div>
      {open ? (
        <div className="bpe-section__body" id={id}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

export function Badge({ tone, children, style }: { tone?: "accent" | "warning" | "danger" | "success"; children: ReactNode; style?: CSSProperties }) {
  return (
    <span className={cx("bpe-badge", tone && `bpe-badge--${tone}`)} style={style}>
      {children}
    </span>
  );
}

export function EmptyState({ icon, title, children }: { icon?: string; title: ReactNode; children?: ReactNode }) {
  return (
    <div className="bpe-empty">
      {icon ? <Icon name={icon} size={22} /> : null}
      <div className="bpe-empty__title">{title}</div>
      {children ? <div>{children}</div> : null}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="bpe-kbd">{children}</kbd>;
}
